import { Empty, Spin, theme, Timeline } from 'antd';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { BatchLog } from '../types';
import { formatBatchTime } from '../utils/formatUtils';

const NS = 'wms.sortingBatch';

/** 非最新事件的圆点颜色（原型 #d6d9de） */
const DOT_GRAY = '#d6d9de';

/** 包裹描述：单件「包裹：X」，多件「包裹：X 等 N 件」 */
const parcelLine = (log: BatchLog, t: TFunction): string | null => {
    if (!log.parcelNumber) return null;
    const count = log.parcelCount || 1;
    return count > 1
        ? t(`${NS}.logs.meta.parcels`, { number: log.parcelNumber, total: count })
        : t(`${NS}.logs.meta.parcel`, { number: log.parcelNumber });
};

/**
 * 移出去向：有目标批次为「移入批次：X」，否则「移出后：未进入其他批次」——
 * 整包移入的包牌移出本仓批次后可能还在上游批次，不能说成「不属于任何批次」
 */
const moveOutLine = (log: BatchLog, t: TFunction): string => (log.targetBatch
    ? t(`${NS}.logs.meta.targetBatch`, { value: log.targetBatch })
    : t(`${NS}.logs.meta.afterMoveOut`, { value: t(`${NS}.bagDetail.segment.leaveToNone`) }));

/** 操作来源 + 操作人 / 角色 / 站点（原型 who），缺省的字段不展示；角色复用全局账号角色文案 */
const operatorLines = (log: BatchLog, t: TFunction): string[] => [
    log.source ? t(`${NS}.logs.meta.source`, { value: t(`${NS}.logs.source.${log.source}`, { defaultValue: log.source }) }) : null,
    log.operator ? t(`${NS}.logs.meta.operator`, { value: log.operator }) : null,
    log.operatorRole ? t(`${NS}.logs.meta.operatorRole`, { value: t(`enum.role.${log.operatorRole}`, { defaultValue: log.operatorRole }) }) : null,
    log.site ? t(`${NS}.logs.meta.site`, { value: log.site }) : null,
].filter((x): x is string => !!x);

/** 每类事件的说明行（转写自原型 curLog / runMoveIn / runMoveOut / createBatch 的日志正文） */
export const logMetaLines = (log: BatchLog, t: TFunction): string[] => {
    const lines: (string | null)[] = [];
    switch (log.type) {
        case 'CREATED':
            if (log.sorterNo) {
                lines.push(
                    t(`${NS}.logs.meta.creationMethod`, { value: t(`${NS}.creationMethodFull.SORTER`) }),
                    t(`${NS}.logs.meta.sorter`, { value: log.sorterNo }),
                    t(`${NS}.logs.meta.sorterReason`),
                );
            } else {
                lines.push(t(`${NS}.logs.meta.creationMethod`, { value: t(`${NS}.creationMethodFull.MANUAL`) }));
            }
            break;
        case 'SORTING_ENDED':
            if (log.syncByBatch) lines.push(t(`${NS}.logs.meta.syncEnded`, { batch: log.syncByBatch }));
            break;
        case 'PARCEL_MOVED_IN':
            lines.push(
                parcelLine(log, t),
                t(`${NS}.logs.meta.targetBag`, { value: log.targetBagNumber || t(`${NS}.logs.meta.newBag`) }),
                log.sourceBatch ? t(`${NS}.logs.meta.sourceBatch`, { value: log.sourceBatch }) : null,
            );
            break;
        case 'PARCEL_MOVED_OUT':
            lines.push(parcelLine(log, t), moveOutLine(log, t));
            break;
        case 'BAG_MOVED_IN':
            lines.push(
                log.bagNumber ? t(`${NS}.logs.meta.bag`, { value: log.bagNumber }) : null,
                log.sourceBatch ? t(`${NS}.logs.meta.sourceBatch`, { value: log.sourceBatch }) : null,
            );
            break;
        case 'BAG_MOVED_OUT':
            lines.push(log.bagNumber ? t(`${NS}.logs.meta.bag`, { value: log.bagNumber }) : null, moveOutLine(log, t));
            break;
        default:
            break;
    }
    return [...lines.filter((x): x is string => !!x), ...operatorLines(log, t)];
};

interface BatchOperationTimelineProps {
    /** 已按时间倒序 */
    logs: BatchLog[];
    loading?: boolean;
    /** 查询失败且没有数据 */
    isFailed?: boolean;
}

/** 批次操作历史：时间倒序，最新一条圆点高亮 */
export const BatchOperationTimeline: React.FC<BatchOperationTimelineProps> = ({ logs, loading = false, isFailed = false }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();

    const items = logs.map((log, i) => ({
        key: `${log.type}-${log.time}-${i}`,
        color: i === 0 ? token.colorPrimary : DOT_GRAY,
        children: (
            <div data-testid="batch-log-item">
                <div className="flex items-baseline gap-2">
                    <span className="flex-1 text-[13px] font-medium text-[#1d2129]">{t(`${NS}.logs.event.${log.type}`)}</span>
                    <span className="flex-none text-[11px] text-[#86909c]">{formatBatchTime(log.time)}</span>
                </div>
                <div className="mt-1 text-xs leading-[1.8] text-[#86909c]">
                    {logMetaLines(log, t).map((line, k) => <div key={`${k}:${line}`}>{line}</div>)}
                </div>
            </div>
        ),
    }));

    return (
        <div>
            <div className="mb-[22px] text-sm font-medium text-[#1d2129]">{t(`${NS}.logs.title`)}</div>
            <Spin spinning={loading}>
                {/* 加载中不显示「暂无操作记录」；查询失败时提示请求失败，不能说成没有记录 */}
                {items.length || loading
                    ? <Timeline items={items} />
                    : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t(isFailed ? `${NS}.common.requestFailed` : `${NS}.logs.empty`)} />}
            </Spin>
        </div>
    );
};
